import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { palette } from '../../../style-utils/theme';
import * as api from '../../../api/admin-client';
import { useAdminList, useAdminResource } from './use-admin';
import {
  AdminTable,
  Id,
  Tag,
  StatusTag,
  labelFor,
  toneFor,
  when,
  num,
  zoneCell,
} from './admin-table';
import { ConfirmDialog, ConfirmSpec } from './elevate-dialog';
import { AdminPanel as Panel, AdminFacts as Facts, AdminLoading as Loading, productsLabel } from './admin-screens';
// IS8.0 (IS8-D4): with Lobbero Stays switched off on the server, no Stays tile,
// switch or products card is drawn (the reads carry no Stays field either).
import { useStaysProduct } from '../../../utils/app-features';
import {
  Card,
  CardHead,
  CardTitle,
  CardBody,
  Toolbar,
  Btn,
  Empty,
  ErrorBox,
  NoteBox,
  Split,
  TileGrid,
  Tile,
  TileLabel,
  TileValue,
  TileFoot,
  Bar,
  Mono,
} from './admin.styled';

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;
const n = (v: unknown) => num(v);

/**
 * THE DETAIL SCREENS — one entity, everything about it, one page.
 *
 * ── WHY THESE ARE NOT LISTS WITH A DRAWER ──────────────────────────────────
 *
 * The brief's actual requirement for the person page is "understand the user
 * without navigating through ten unrelated screens", and a drawer over a list
 * cannot hold an account's profile, its organisation, its buildings, its
 * grants, its residencies, its devices, its recent actions, its shifts AND its
 * billing context. They get their own URL for the same reason: an investigation
 * gets pasted into a ticket.
 *
 * ── EVERY RELATIONSHIP IS NAVIGABLE, BOTH WAYS ─────────────────────────────
 *
 * Organization → people → this person → their buildings → that building → its
 * units → its residents → back to the organisation. Each hop is a link, and
 * every list row that names another entity links to it.
 *
 * ── ACTIONS ASK THE PARENT ─────────────────────────────────────────────────
 *
 * A detail screen never elevates or confirms by itself. It hands a ConfirmSpec
 * up to the shell, which owns the step-up state — so the "you need to
 * re-authenticate" flow is written once and cannot be half-implemented on one
 * screen.
 */

export interface ActionHost {
  /** Runs `spec` once the operator is elevated, prompting for the password if not. */
  requestAction: (spec: ConfirmSpec) => void;
  elevated: boolean;
}

const Back: React.FC<{ to: string; label: string }> = ({ to, label }) => {
  const nav = useNavigate();

  return (
    <Btn onClick={() => nav(to)}>
      ← {label}
    </Btn>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ORGANIZATION
   ═══════════════════════════════════════════════════════════════════════════ */

/** Words for a product change's source (org_product_events.source). */
const PRODUCT_SOURCE: Record<string, string> = {
  console: 'Super Admin',
  seed: 'a test seed',
  signup: 'sign-up',
  purchase: 'a purchase',
  cancel: 'a cancellation',
  merge: 'an account merge',
  close: 'account closure',
};

/**
 * Independent Stays IS1 (IS1.8): which Lobbero products the organisation holds,
 * the elevated switches, and the last 20 changes. Adding Desk is a purchase
 * (the server refuses it from here, 409 DESK_IS_A_PURCHASE) and neither product
 * is refused (PRODUCTS_NEED_ONE) — the server's own message is what the
 * operator reads.
 */
const ProductsCard: React.FC<{ org: Row; data: Row; requestAction: (spec: ConfirmSpec) => void; reload: () => void }> = ({
  org,
  data,
  requestAction,
  reload,
}) => {
  const p = data.products || { desk: true, stays: false };
  const events: Row[] = data.product_events || [];
  const label = productsLabel(p.desk, p.stays);
  const name = (product: 'desk' | 'stays') => (product === 'desk' ? 'Desk' : 'Stays');
  // IS6 (R-IS6.9): Desk as a complimentary grant, where no money can move.
  const grantDesk = () =>
    requestAction({
      title: `Give Lobbero Desk free to ${org.name}`,
      body: (
        <>
          A complimentary grant: no money moves. The server refuses it for a customer who pays; they add Desk from
          Billing. Units and buildings hidden when Desk was removed come back.
        </>
      ),
      confirmLabel: 'Give Desk free',
      danger: false,
      onConfirm: async reason => {
        await api.setOrgProducts(org.id, { desk: true, comped: true }, reason);
        toast.success('Products changed');
        reload();
      },
    });
  const ask = (product: 'desk' | 'stays', on: boolean) =>
    requestAction({
      title: `Switch Lobbero ${name(product)} ${on ? 'on' : 'off'} for ${org.name}`,
      body:
        product === 'desk' && on ? (
          <>Adding Desk is normally a purchase. The server refuses it unless test mode allows it.</>
        ) : product === 'desk' ? (
          <>
            Desk screens, concierges and residents disappear for this customer. Open shifts close, and units and
            buildings with no listing are hidden until Desk comes back. Their plan and bill do not change from here.
            Nothing is deleted.
          </>
        ) : on ? (
          <>This customer&apos;s owners and managers get the Stays section.</>
        ) : (
          <>The Stays section disappears for this customer. Refused while a stay is still to come.</>
        ),
      confirmLabel: `Turn ${name(product)} ${on ? 'on' : 'off'}`,
      danger: !on,
      onConfirm: async reason => {
        await api.setOrgProducts(org.id, { [product]: on }, reason);
        toast.success('Products changed');
        reload();
      },
    });

  return (
    <Panel title="Products">
      <div data-testid="admin-products-card">
        <Facts
          rows={[
            ['Products', <Tag key="p" tone={label.tone}>{label.text}</Tag>],
            ['Lobbero Desk', p.desk ? 'On' : 'Off'],
            ['Lobbero Stays', p.stays ? 'On' : 'Off'],
          ]}
        />
        <Toolbar>
          {/* "Turn …", not "Switch …": the Estate panel's Stays switch keeps its own name. */}
          <Btn onClick={() => ask('desk', !p.desk)}>{p.desk ? 'Turn Desk off' : 'Turn Desk on'}</Btn>
          {!p.desk ? <Btn onClick={grantDesk}>Give Desk free</Btn> : null}
          {/* Stays follows the platform policy (flag_switch_owner), like the Estate switch. */}
          {data.stays_console_switch ? (
            <Btn onClick={() => ask('stays', !p.stays)}>{p.stays ? 'Turn Stays off' : 'Turn Stays on'}</Btn>
          ) : null}
        </Toolbar>
        {events.length ? (
          <AdminTable<Row>
            rows={events}
            rowKey={e => e.id}
            columns={[
              { key: 'when', header: 'When', nowrap: true, render: e => when(e.created_at) },
              { key: 'product', header: 'Product', render: e => (e.product === 'desk' ? 'Desk' : 'Stays') },
              { key: 'change', header: 'Change', render: e => `${e.from_on ? 'On' : 'Off'} → ${e.to_on ? 'On' : 'Off'}` },
              { key: 'by', header: 'By', render: e => PRODUCT_SOURCE[e.source] || e.source },
              { key: 'reason', header: 'Reason', wide: true, render: e => e.reason || '—' },
            ]}
          />
        ) : (
          <Empty>No product changes yet.</Empty>
        )}
      </div>
    </Panel>
  );
};

export const OrganizationDetail: React.FC<ActionHost> = ({ requestAction }) => {
  const staysOn = useStaysProduct() === true;
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data, loading, error, notFound, reload } = useAdminResource<Row>(
    `/organizations/${encodeURIComponent(id)}`
  );
  const people = useAdminList<Row>(`organizations/${encodeURIComponent(id)}/users`, {});
  const buildings = useAdminResource<Row>(`/organizations/${encodeURIComponent(id)}/buildings`);
  const billing = useAdminResource<Row>(`/organizations/${encodeURIComponent(id)}/billing`);
  const activity = useAdminList<Row>(`organizations/${encodeURIComponent(id)}/activity`, {});

  if (notFound) return <Empty>No organization with that id.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const org = data.organization;
  const t = data.tallies;
  const a = data.allowance;
  const suspended = org.status !== 'active';

  return (
    <>
      <Toolbar>
        <Back to="/admin/organizations" label="All organizations" />
        <div style={{ flex: 1 }} />
        {suspended ? (
          <Btn
            variant="primary"
            onClick={() =>
              requestAction({
                title: `Restore ${org.name}`,
                body: (
                  <>
                    Everyone in this organization gets their access back immediately. Anybody
                    still signed in carries on where they were.
                  </>
                ),
                confirmLabel: 'Restore access',
                onConfirm: async reason => {
                  await api.restoreOrg(org.id, reason);
                  toast.success('Organization restored');
                  reload();
                },
              })
            }
          >
            Restore access
          </Btn>
        ) : (
          <Btn
            variant="danger"
            onClick={() =>
              requestAction({
                title: `Suspend ${org.name}`,
                body: (
                  <>
                    <strong>Every one of this customer&apos;s {n(t.users_active)} active
                    people will be locked out at once and unable to sign in</strong>, including
                    the account owner. Each of them sees a notice that the organization is
                    suspended. Their data is untouched and the change is reversible from this
                    screen.
                  </>
                ),
                typeToConfirm: org.name,
                confirmLabel: 'Suspend organization',
                danger: true,
                onConfirm: async reason => {
                  await api.suspendOrg(org.id, reason);
                  toast.success('Organization suspended');
                  reload();
                },
              })
            }
          >
            Suspend organization
          </Btn>
        )}
      </Toolbar>

      {suspended ? (
        <NoteBox>
          This organization is <strong>{org.status}</strong>. Nobody in it can sign in.
        </NoteBox>
      ) : null}

      <Split>
        <Panel title="Account">
          <Facts
            rows={[
              ['Name', org.name],
              ['Id', <Mono key="id">{org.id}</Mono>],
              ['Status', <Tag key="s" tone={toneFor(org.status)}>{org.status}</Tag>],
              [
                'Owner',
                org.owner_user_id ? (
                  <Btn key="o" onClick={() => nav(`/admin/users/${encodeURIComponent(org.owner_user_id)}`)}>
                    {org.owner_name || org.owner_user_id}
                  </Btn>
                ) : (
                  '—'
                ),
              ],
              ['Owner email', org.owner_email || '—'],
              ['Created', when(org.createdAt)],
              ['Last audited action', when(t.last_audit_at)],
            ]}
          />
        </Panel>

        <Panel title="Estate">
          <TileGrid>
            <Tile>
              <TileLabel>People</TileLabel>
              <TileValue>{n(t.users_count)}</TileValue>
              <TileFoot>{n(t.users_active)} active</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Buildings</TileLabel>
              <TileValue>{n(t.buildings_count)}</TileValue>
              <TileFoot>{n(t.portfolios_count)} portfolios</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Units</TileLabel>
              <TileValue>{n(t.units_total)}</TileValue>
              <TileFoot>{n(t.units_archived)} returned</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Residents</TileLabel>
              <TileValue>{n(t.residents_open)}</TileValue>
              <TileFoot>{n(t.invites_pending)} invites pending</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Visitor entries</TileLabel>
              <TileValue>{n(t.visitor_entries)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Building activities</TileLabel>
              <TileValue>{n(t.activity_entries)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Live visitor passes</TileLabel>
              <TileValue>{n(t.passes_live)}</TileValue>
            </Tile>
            {/* Lobbero Stays (plan S1): the organisation flag and its listing
                COUNTS — never a listing name. */}
            {staysOn ? (<>
            <Tile data-testid="admin-stays-tile">
              <TileLabel>Lobbero Stays</TileLabel>
              <TileValue>{n(t.stays_listings_active)}</TileValue>
              <TileFoot>
                {t.stays_enabled
                  ? `On · ${n(t.stays_listings_paused)} paused · ${n(t.stays_listings_draft)} draft`
                  : 'Off'}
              </TileFoot>
            </Tile>
            {/* Lobbero Stays (plan S4, R-S4.38): stays and owner blocks as COUNTS
                only — never a guest, a note or an amount. */}
            <Tile data-testid="admin-stays-upcoming-tile">
              <TileLabel>Upcoming stays</TileLabel>
              <TileValue>{n(t.stays_upcoming)}</TileValue>
            </Tile>
            <Tile data-testid="admin-stays-total-tile">
              <TileLabel>Stays</TileLabel>
              <TileValue>{n(t.stays_total)}</TileValue>
            </Tile>
            <Tile data-testid="admin-stays-blocks-tile">
              <TileLabel>Upcoming owner blocks</TileLabel>
              <TileValue>{n(t.stays_blocks_upcoming)}</TileValue>
            </Tile>
            </>) : null}
          </TileGrid>
          {/* The switch exists only while the platform policy lets the console
              own it (client question Q21, flag_switch_owner = console). */}
          {staysOn && data.stays_console_switch ? (
            <Toolbar>
              <Btn
                onClick={() =>
                  requestAction({
                    title: t.stays_enabled
                      ? `Switch Lobbero Stays off for ${org.name}`
                      : `Switch Lobbero Stays on for ${org.name}`,
                    body: t.stays_enabled ? (
                      <>
                        The Stays section disappears for this customer&apos;s owners and
                        managers. Their {n(t.stays_listings_active)} live listings are kept
                        and come back when Stays is switched on again.
                      </>
                    ) : (
                      <>
                        This customer&apos;s owners and managers get the Stays section and can
                        list units for short-term guests in buildings they switch on.
                      </>
                    ),
                    confirmLabel: t.stays_enabled ? 'Switch Stays off' : 'Switch Stays on',
                    onConfirm: async reason => {
                      await api.setOrgStays(org.id, !t.stays_enabled, reason);
                      toast.success(t.stays_enabled ? 'Lobbero Stays switched off' : 'Lobbero Stays switched on');
                      reload();
                    },
                  })
                }
              >
                {t.stays_enabled ? 'Switch Stays off' : 'Switch Stays on'}
              </Btn>
            </Toolbar>
          ) : null}
        </Panel>
      </Split>

      {staysOn ? <ProductsCard org={org} data={data} requestAction={requestAction} reload={reload} /> : null}

      {a ? (
        <Panel
          title="Unit allowance"
          foot={
            <NoteBox>
              From <Mono>org_unit_allowance()</Mono> — the same function the product enforces
              against. The console does not compute this itself.
            </NoteBox>
          }
        >
          <TileGrid>
            <Tile>
              <TileLabel>Bought</TileLabel>
              <TileValue>{n(a.bought)}</TileValue>
            </Tile>
            <Tile tone={Number(a.used) > Number(a.bought) ? 'alert' : undefined}>
              <TileLabel>Used</TileLabel>
              <TileValue>{n(a.used)}</TileValue>
              <TileFoot>
                <Bar
                  pct={(Number(a.used) / Math.max(1, Number(a.bought))) * 100}
                  tone={Number(a.used) > Number(a.bought) ? 'alert' : undefined}
                />
              </TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Remaining</TileLabel>
              <TileValue>{n(a.remaining)}</TileValue>
            </Tile>
          </TileGrid>
        </Panel>
      ) : null}

      <Panel title="Subscriptions">
        <AdminTable<Row>
          rows={data.subscriptions}
          rowKey={r => String(r.id)}
          emptyLabel="This organization has never had a subscription."
          columns={[
            { key: 'p', header: 'Plan', render: r => r.plan_code || r.plan_name || '—' },
            // Independent Stays IS4: a Stays pool counts places to stay.
            { key: 'l', header: 'Product', render: r => (r.product_line === 'stays' ? 'Stays' : 'Desk') },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'u', header: 'Units', numeric: true, render: r => n(r.unit_quantity) },
            {
              key: 'e',
              header: 'Period ends',
              // Same rule as the subscriptions list: without a Stripe object the
              // period is never advanced, so the date would read as stale billing.
              render: r =>
                r.stripe_subscription_id ? when(r.current_period_end) : <Tag tone="neutral">not billed · no Stripe</Tag>,
            },
            { key: 't', header: 'Trial ends', wide: true, render: r => when(r.trial_end) },
            {
              key: 'c',
              header: 'Cancels',
              render: r => (r.cancel_at_period_end ? <Tag tone="warning">at period end</Tag> : '—'),
            },
            { key: 'x', header: 'Stripe', wide: true, render: r => <Id value={r.stripe_subscription_id} /> },
          ]}
        />
      </Panel>

      {/* Independent Stays IS4: the Stays subscription (listing slots or the
          per-check-in fee) and the metered check-ins, counts only. */}
      {billing.data && billing.data.stays ? (
        <Panel title="Stays billing">
          <AdminTable<Row>
            rows={((billing.data.stays as Row).subscriptions as Row[]) || []}
            rowKey={r => String(r.id)}
            emptyLabel="No Stays subscription. A per-property host pays on the plan above."
            columns={[
              { key: 'k', header: 'Kind', render: r => (r.kind === 'addon' ? 'Listing slots' : 'Per check-in fee') },
              { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
              { key: 'q', header: 'Slots', numeric: true, render: r => (r.kind === 'addon' ? n(r.listing_quantity) : '—') },
              { key: 'p', header: 'Provider', render: r => (r.comped ? <Tag tone="neutral">comped</Tag> : r.provider) },
              { key: 'e', header: 'Period ends', wide: true, render: r => when(r.current_period_end) },
              { key: 'x', header: 'Stripe', wide: true, render: r => <Id value={r.stripe_subscription_id} /> },
            ]}
          />
          <Facts
            rows={(((billing.data.stays as Row).usage_since_last_month as Row[]) || []).map(u => [
              `check-ins ${String(u.status).replace(/_/g, ' ')}`,
              `${n(u.n)}`,
            ])}
          />
        </Panel>
      ) : null}

      {billing.data ? (
        <Panel
          title="Billing authority"
          foot={
            <NoteBox>
              Fields marked <em>stripe</em> are a local mirror of the payment provider. If
              they disagree, Stripe is right and the mirror is stale — reconcile from the
              customer&apos;s own billing screen rather than editing anything here.
            </NoteBox>
          }
        >
          <Facts
            rows={Object.entries(billing.data.authority as Record<string, string>).map(([k, v]) => [
              k.replace(/_/g, ' '),
              <Mono key={k}>{v}</Mono>,
            ])}
          />
        </Panel>
      ) : null}

      <Panel title="People">
        <AdminTable<Row>
          rows={people.items}
          rowKey={r => String(r.id)}
          loading={people.loading}
          error={people.error}
          total={people.total}
          hasMore={people.hasMore}
          onLoadMore={people.loadMore}
          onRowClick={r => nav(`/admin/users/${encodeURIComponent(r.id)}`)}
          columns={[
            { key: 'n', header: 'Name', render: r => r.full_name || <Id value={r.id} /> },
            { key: 'e', header: 'Email', render: r => r.email },
            {
              key: 'r',
              header: 'Role',
              render: r =>
                r.role ? <Tag tone="brand">{r.role.replace('_', ' ')}</Tag> :
                r.open_occupancies ? <Tag tone="neutral">resident</Tag> : '—',
            },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'l', header: 'Sessions', numeric: true, wide: true, render: r => n(r.live_sessions) },
            { key: 'c', header: 'Joined', wide: true, render: r => when(r.createdAt) },
          ]}
        />
      </Panel>

      <Panel title="Buildings">
        <AdminTable<Row>
          rows={buildings.data?.items || []}
          rowKey={r => String(r.id)}
          loading={buildings.loading}
          onRowClick={r => nav(`/admin/buildings/${r.id}`)}
          emptyLabel="No buildings yet."
          columns={[
            { key: 'n', header: 'Building', render: r => r.property_name },
            { key: 'c', header: 'City', render: r => r.city || '—' },
            { key: 'p', header: 'Portfolio', wide: true, render: r => r.portfolio_name || '—' },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'u', header: 'Units', numeric: true, render: r => n(r.units_total) },
            { key: 'r', header: 'Residents', numeric: true, render: r => n(r.residents) },
            { key: 'l', header: 'Last visitor', wide: true, render: r => when(r.last_visitor_at) },
          ]}
        />
      </Panel>

      <Panel
        title="Their activity log"
        foot={
          <NoteBox>
            This is the customer&apos;s OWN audit trail — the one their account owner can
            already read. Anything this console does to them appears here too, by design.
          </NoteBox>
        }
      >
        <AdminTable<Row>
          rows={activity.items}
          rowKey={r => String(r.id)}
          loading={activity.loading}
          hasMore={activity.hasMore}
          onLoadMore={activity.loadMore}
          emptyLabel="Nothing recorded."
          columns={[
            { key: 'w', header: 'When', render: r => when(r.created_at) },
            { key: 'a', header: 'Action', render: r => <Mono>{r.action}</Mono> },
            { key: 'e', header: 'Entity', render: r => `${r.entity_type || ''} ${String(r.entity_id || '').slice(0, 10)}` },
            { key: 'b', header: 'By', wide: true, render: r => r.actor_name || <Id value={r.actor_user_id} /> },
          ]}
        />
      </Panel>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   USER
   ═══════════════════════════════════════════════════════════════════════════ */

export const UserDetail: React.FC<ActionHost> = ({ requestAction }) => {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data, loading, error, notFound, reload } = useAdminResource<Row>(
    `/users/${encodeURIComponent(id)}`
  );

  if (notFound) return <Empty>No account with that id.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const u = data;
  const liveSessions = (u.sessions || []).filter((s: Row) => !s.revoked_at).length;

  return (
    <>
      <Toolbar>
        <Back to="/admin/users" label="All people" />
        <div style={{ flex: 1 }} />
        {u.status === 'active' ? (
          <Btn
            variant="danger"
            disabled={u.is_org_owner}
            title={
              u.is_org_owner
                ? 'This person owns their organization. Suspend the organization instead.'
                : undefined
            }
            onClick={() =>
              requestAction({
                title: `Deactivate ${u.full_name}`,
                body: (
                  <>
                    They will be signed out of every device immediately and will not be able
                    to sign back in. Their records stay exactly as they are.
                  </>
                ),
                typeToConfirm: u.email,
                confirmLabel: 'Deactivate account',
                danger: true,
                onConfirm: async reason => {
                  await api.deactivateUser(u.id, reason);
                  toast.success('Account deactivated');
                  reload();
                },
              })
            }
          >
            Deactivate
          </Btn>
        ) : (
          <Btn
            variant="primary"
            onClick={() =>
              requestAction({
                title: `Reactivate ${u.full_name}`,
                body: <>They will be able to sign in again. They will need their password.</>,
                confirmLabel: 'Reactivate account',
                onConfirm: async reason => {
                  await api.reactivateUser(u.id, reason);
                  toast.success('Account reactivated');
                  reload();
                },
              })
            }
          >
            Reactivate
          </Btn>
        )}
        <Btn
          disabled={!liveSessions}
          onClick={() =>
            requestAction({
              title: `Sign ${u.full_name} out everywhere`,
              body: (
                <>
                  {liveSessions} live session{liveSessions === 1 ? '' : 's'} will be revoked and
                  every issued token invalidated. They keep their password and can sign back in.
                </>
              ),
              confirmLabel: 'Revoke all sessions',
              onConfirm: async reason => {
                const r = await api.revokeSessions(u.id, reason);
                toast.success(`Revoked ${r.revoked} session${r.revoked === 1 ? '' : 's'}`);
                reload();
              },
            })
          }
        >
          Revoke sessions ({liveSessions})
        </Btn>
      </Toolbar>

      {u.platform_admin && !u.platform_admin.revoked_at ? (
        <NoteBox>
          This account is a <strong>platform administrator</strong> — granted{' '}
          {when(u.platform_admin.granted_at)}. It can read every organization on the platform.
        </NoteBox>
      ) : null}

      <Split>
        <Panel title="Account">
          <Facts
            rows={[
              ['Name', u.full_name],
              ['Email', u.email],
              ['Phone', u.phone_number || '—'],
              ['Id', <Mono key="i">{u.id}</Mono>],
              [
                'Role',
                u.role ? <Tag key="r" tone="brand">{u.role.replace('_', ' ')}</Tag> :
                  <span key="r" style={{ color: palette.slateLight }}>no staff role</span>,
              ],
              ['Status', <Tag key="s" tone={toneFor(u.status)}>{u.status}</Tag>],
              [
                'Organization',
                u.organization_id ? (
                  <Btn key="o" onClick={() => nav(`/admin/organizations/${encodeURIComponent(u.organization_id)}`)}>
                    {u.org_name || u.organization_id}
                  </Btn>
                ) : '—',
              ],
              ['Owns the organization', u.is_org_owner ? 'yes' : 'no'],
              [
                'Email verified',
                u.email_verified_at ? when(u.email_verified_at) : <Tag key="v" tone="warning">not verified</Tag>,
              ],
              ['Pending email change', u.pending_email || '—'],
              ['Onboarding', u.onboarding_completed ? 'complete' : 'incomplete'],
              ['Joined', when(u.createdAt)],
              [
                'Locked out',
                u.locked_until && new Date(u.locked_until) > new Date()
                  ? <Tag key="l" tone="alert">until {when(u.locked_until)}</Tag>
                  : `no (${n(u.failed_login_attempts)} recent failures)`,
              ],
            ]}
          />
        </Panel>

        <Panel title="Billing context">
          {u.org_allowance ? (
            <Facts
              rows={[
                ['Plan', u.org_subscription?.plan_code || '—'],
                [
                  'Subscription',
                  u.org_subscription ? (
                    <Tag key="s" tone={toneFor(u.org_subscription.status)}>{u.org_subscription.status}</Tag>
                  ) : 'none',
                ],
                ['Units bought', n(u.org_allowance.bought)],
                ['Units used', n(u.org_allowance.used)],
                ['Units remaining', n(u.org_allowance.remaining)],
              ]}
            />
          ) : (
            <Empty>No organization, so no billing context.</Empty>
          )}
        </Panel>
      </Split>

      <Split>
        <Panel title="Buildings they can reach">
          <AdminTable<Row>
            rows={u.buildings || []}
            rowKey={r => `${r.id}-${r.via}`}
            emptyLabel="No building access."
            onRowClick={r => nav(`/admin/buildings/${r.id}`)}
            columns={[
              { key: 'n', header: 'Building', render: r => r.property_name },
              { key: 'c', header: 'City', render: r => r.city || '—' },
              { key: 'v', header: 'Via', render: r => <Mono>{r.via}</Mono> },
            ]}
          />
        </Panel>
        <Panel title="Access grants">
          <AdminTable<Row>
            rows={u.grants || []}
            rowKey={r => String(r.id)}
            emptyLabel="No explicit grants."
            columns={[
              { key: 's', header: 'Scope', render: r => r.scope_type },
              {
                key: 't',
                header: 'Target',
                render: r => r.building_id || r.portfolio_id || r.unit_id || 'organization',
              },
              {
                key: 'p',
                header: 'Power',
                render: r =>
                  r.can_administer ? <Tag tone="warning">administer</Tag> :
                  r.can_operate ? <Tag tone="neutral">operate</Tag> : '—',
              },
            ]}
          />
        </Panel>
      </Split>

      <Panel
        title="Residencies"
        foot={
          <NoteBox>
            Since migration 037 living somewhere is an occupancy, not a role — which is why a
            person with no staff role can still hold the resident portal.
          </NoteBox>
        }
      >
        <AdminTable<Row>
          rows={u.occupancies || []}
          rowKey={r => String(r.id)}
          emptyLabel="This person does not live in any unit."
          columns={[
            { key: 'n', header: 'Named as', render: r => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—' },
            { key: 'u', header: 'Unit', render: r => r.unit_number || '—' },
            { key: 'b', header: 'Building', render: r => r.property_name || '—' },
            { key: 'r', header: 'Relationship', wide: true, render: r => r.relationship || '—' },
            { key: 'i', header: 'Moved in', render: r => (r.moved_in ? String(r.moved_in).slice(0, 10) : '—') },
            {
              key: 'o',
              header: 'Moved out',
              render: r => (r.moved_out ? String(r.moved_out).slice(0, 10) : <Tag tone="success">current</Tag>),
            },
          ]}
        />
      </Panel>

      <Split>
        <Panel title="Devices">
          <AdminTable<Row>
            rows={u.sessions || []}
            rowKey={r => String(r.id)}
            emptyLabel="No sessions."
            columns={[
              { key: 'd', header: 'Device', render: r => <span title={r.user_agent}>{String(r.user_agent || '').slice(0, 40) || 'unknown'}</span> },
              { key: 'i', header: 'IP', wide: true, render: r => <Mono>{r.ip || '—'}</Mono> },
              { key: 'l', header: 'Last used', render: r => when(r.last_used_at) },
              {
                key: 's',
                header: 'State',
                render: r =>
                  r.revoked_at ? <Tag tone="alert">revoked</Tag> :
                  new Date(r.expires_at) < new Date() ? <Tag tone="neutral">expired</Tag> :
                  <Tag tone="success">live</Tag>,
              },
            ]}
          />
        </Panel>
        <Panel title="Recent actions they took">
          <AdminTable<Row>
            rows={u.recent_actions || []}
            rowKey={r => `${r.created_at}${r.action}${r.entity_id}`}
            emptyLabel="Nothing recorded."
            columns={[
              { key: 'w', header: 'When', render: r => when(r.created_at) },
              { key: 'a', header: 'Action', render: r => <Mono>{r.action}</Mono> },
              { key: 'e', header: 'On', render: r => r.entity_type || '—' },
            ]}
          />
        </Panel>
      </Split>

      <Panel title="Shifts">
        <AdminTable<Row>
          rows={u.shifts || []}
          rowKey={r => String(r.id)}
          emptyLabel="This person has never clocked in."
          columns={[
            { key: 'b', header: 'Building', render: r => r.property_name || '—' },
            { key: 's', header: 'Started', render: r => when(r.started_at) },
            { key: 'e', header: 'Ended', render: r => (r.ended_at ? when(r.ended_at) : <Tag tone="success">open</Tag>) },
          ]}
        />
      </Panel>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   BUILDING
   ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════
   PORTFOLIO
   ═══════════════════════════════════════════════════════════════════════════

   A portfolio row used to be a dead end — the list rendered it and the click
   did nothing. It is the layer between an organisation and its buildings, so
   this page answers the two questions that layer exists for: whose is it, and
   what is under it.

   The owner is named at the top rather than only the organisation, because the
   next thing an operator does after opening a portfolio is contact somebody.
   ═══════════════════════════════════════════════════════════════════════════ */

export const PortfolioDetail: React.FC = () => {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data, loading, error, notFound } = useAdminResource<Row>(
    `/portfolios/${encodeURIComponent(id)}`
  );

  if (notFound) return <Empty>No portfolio with that id.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const pf = data.portfolio;
  const t = data.tallies;

  return (
    <>
      <Toolbar>
        <Back to="/admin/portfolios" label="All portfolios" />
        <Btn onClick={() => nav(`/admin/organizations/${encodeURIComponent(pf.organization_id)}`)}>
          {pf.org_name || pf.organization_id}
        </Btn>
        {pf.owner_user_id ? (
          <Btn onClick={() => nav(`/admin/users/${encodeURIComponent(pf.owner_user_id)}`)}>
            {pf.owner_name || pf.owner_email || 'Owner'}
          </Btn>
        ) : null}
      </Toolbar>

      <Split>
        <Panel title={pf.name}>
          <Facts
            rows={[
              ['Code', pf.code ? <Mono key="c">{pf.code}</Mono> : '—'],
              ['Status', <Tag key="s" tone={toneFor(pf.status)}>{pf.status}</Tag>],
              ['Default', pf.is_default ? <Tag key="d" tone="brand">default</Tag> : '—'],
              ['Organization', pf.org_name || <Id key="o" value={pf.organization_id} />],
              ['Owner', pf.owner_name || '—'],
              ['Owner email', pf.owner_email || '—'],
              ['Created', when(pf.createdAt)],
            ]}
          />
        </Panel>

        <Panel title="What is under it">
          <TileGrid>
            <Tile>
              <TileLabel>Buildings</TileLabel>
              <TileValue>{n(t.buildings_total)}</TileValue>
              <TileFoot>{n(t.buildings_active)} active</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Units</TileLabel>
              <TileValue>{n(t.units_total)}</TileValue>
              <TileFoot>{n(t.units_active)} active</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Residents</TileLabel>
              <TileValue>{n(t.residents_open)}</TileValue>
              <TileFoot>current occupancies</TileFoot>
            </Tile>
          </TileGrid>
        </Panel>
      </Split>

      <Panel title="Buildings">
        <AdminTable<Row>
          rows={data.buildings || []}
          rowKey={r => String(r.id)}
          emptyLabel="This portfolio holds no buildings."
          onRowClick={r => nav(`/admin/buildings/${r.id}`)}
          columns={[
            { key: 'b', header: 'Building', render: r => r.property_name || <Id value={r.id} /> },
            {
              key: 'a',
              header: 'Address',
              wide: true,
              render: r => [r.address, r.city, r.country].filter(Boolean).join(', ') || '—',
            },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            { key: 'u', header: 'Units', numeric: true, render: r => n(r.units_total) },
            { key: 'r', header: 'Residents', numeric: true, render: r => n(r.residents) },
            { key: 't', header: 'Building time zone', wide: true, render: r => <Mono>{zoneCell(r.timezone)}</Mono> },
          ]}
        />
      </Panel>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   UNIT
   ═══════════════════════════════════════════════════════════════════════════

   THE OCCUPANCY HISTORY IS THE POINT. "Who lived here before" is the question
   that brings an operator to a single unit, and it is not answerable from the
   residents list — that list is keyed by occupancy and shows the current ones.
   So this page shows every occupancy the unit has ever had, ended included.

   A unit is also the platform's billing atom (units bought against units used),
   so its status is shown with the same words the billing screen uses:
   `archived` is displayed as "returned", because that is what it means to the
   customer and what the return-units flow calls it.
   ═══════════════════════════════════════════════════════════════════════════ */

export const UnitDetail: React.FC = () => {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data, loading, error, notFound } = useAdminResource<Row>(
    `/units/${encodeURIComponent(id)}`
  );

  if (notFound) return <Empty>No unit with that id.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const u = data.unit;
  const t = data.tallies;
  const statusLabel = u.status === 'archived' ? 'returned' : u.status;

  return (
    <>
      <Toolbar>
        <Back to="/admin/units" label="All units" />
        {u.building_id ? (
          <Btn onClick={() => nav(`/admin/buildings/${u.building_id}`)}>
            {u.property_name || `Building ${u.building_id}`}
          </Btn>
        ) : null}
        <Btn onClick={() => nav(`/admin/organizations/${encodeURIComponent(u.organization_id)}`)}>
          {u.org_name || u.organization_id}
        </Btn>
      </Toolbar>

      <Split>
        <Panel title={`Unit ${u.unit_number}`}>
          <Facts
            rows={[
              ['Status', <Tag key="s" tone={toneFor(u.status)}>{statusLabel}</Tag>],
              ['Kind', u.kind || '—'],
              ['Floor', u.floor || '—'],
              ['Bedrooms', u.bedrooms === null || u.bedrooms === undefined ? '—' : n(u.bedrooms)],
              ['Size', u.sq_ft ? `${n(u.sq_ft)} sq ft` : '—'],
              ['Building', u.property_name || '—'],
              ['Address', [u.address, u.city, u.country].filter(Boolean).join(', ') || '—'],
              ['Portfolio', u.portfolio_name || '—'],
              ['Organization', u.org_name || <Id key="o" value={u.organization_id} />],
              ['Owner email', u.owner_email || '—'],
              ['Created', when(u.createdAt)],
            ]}
          />
        </Panel>

        <Panel title="History">
          <TileGrid>
            <Tile>
              <TileLabel>Occupancies</TileLabel>
              <TileValue>{n(t.occupancies_total)}</TileValue>
              <TileFoot>{n(t.occupancies_open)} current</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Parcels</TileLabel>
              <TileValue>{n(t.parcels_total)}</TileValue>
              <TileFoot>{n(t.parcels_open)} on the shelf</TileFoot>
            </Tile>
          </TileGrid>
          <NoteBox>
            Tenancy facts only. Visitor entries, parcel contents and log text about
            this unit stay inside the customer&apos;s own product.
          </NoteBox>
        </Panel>
      </Split>

      <Panel title="Everyone who has lived here">
        <AdminTable<Row>
          rows={data.occupancies || []}
          rowKey={r => String(r.id)}
          emptyLabel="This unit has never been occupied."
          onRowClick={r => (r.user_id ? nav(`/admin/users/${encodeURIComponent(r.user_id)}`) : undefined)}
          columns={[
            {
              key: 'n',
              header: 'Resident',
              render: r => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—',
            },
            { key: 'r', header: 'Relationship', wide: true, render: r => r.relationship || '—' },
            { key: 'p', header: 'Primary', render: r => (r.is_primary ? <Tag tone="brand">primary</Tag> : '—') },
            { key: 'i', header: 'Moved in', render: r => (r.moved_in ? String(r.moved_in).slice(0, 10) : '—') },
            {
              key: 'o',
              header: 'Moved out',
              render: r =>
                r.moved_out ? String(r.moved_out).slice(0, 10) : <Tag tone="success">current</Tag>,
            },
            {
              key: 'a',
              header: 'Account',
              render: r => (r.user_id ? <StatusTag value={r.user_status} /> : 'no login'),
            },
          ]}
        />
      </Panel>
    </>
  );
};

export const BuildingDetail: React.FC = () => {
  const staysOn = useStaysProduct() === true;
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data, loading, error, notFound } = useAdminResource<Row>(`/buildings/${encodeURIComponent(id)}`);
  const units = useAdminList<Row>('units', { building: id });
  const residents = useAdminList<Row>('residents', { building: id, open: 'true' });

  if (notFound) return <Empty>No building with that id.</Empty>;
  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const b = data.building;
  const t = data.tallies;

  return (
    <>
      <Toolbar>
        <Back to="/admin/buildings" label="All buildings" />
        <Btn onClick={() => nav(`/admin/organizations/${encodeURIComponent(b.organization_id)}`)}>
          {b.org_name || b.organization_id}
        </Btn>
      </Toolbar>

      <Split>
        <Panel title={b.property_name}>
          <Facts
            rows={[
              ['Address', [b.address, b.city, b.region, b.postal_code, b.country].filter(Boolean).join(', ') || '—'],
              ['Status', <Tag key="s" tone={toneFor(b.status)}>{b.status}</Tag>],
              ['Building time zone', <Mono key="t">{zoneCell(b.timezone)}</Mono>],
              ['Portfolio', b.portfolio_name || '—'],
              ['Staffing model', b.staffing_model || '—'],
              ['Organization status', <Tag key="o" tone={toneFor(b.org_status)}>{b.org_status}</Tag>],
              ['Acquired', b.acquired_on ? String(b.acquired_on).slice(0, 10) : '—'],
              ['Created', when(b.createdAt)],
              ['Coordinates', b.latitude && b.longitude ? `${b.latitude}, ${b.longitude}` : '—'],
            ]}
          />
        </Panel>

        <Panel title="Activity">
          <TileGrid>
            <Tile>
              <TileLabel>Units</TileLabel>
              <TileValue>{n(t.units_total)}</TileValue>
              <TileFoot>{n(t.units_active)} active · {n(t.units_archived)} returned</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Residents</TileLabel>
              <TileValue>{n(t.residents_open)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>Visitor entries</TileLabel>
              <TileValue>{n(t.visitor_entries)}</TileValue>
              <TileFoot>last {when(t.last_visitor_at)}</TileFoot>
            </Tile>
            <Tile>
              <TileLabel>Activities</TileLabel>
              <TileValue>{n(t.activity_entries)}</TileValue>
              <TileFoot>last {when(t.last_activity_at)}</TileFoot>
            </Tile>
            <Tile tone={Number(t.parcels_open) > 20 ? 'warning' : undefined}>
              <TileLabel>Parcels on the shelf</TileLabel>
              <TileValue>{n(t.parcels_open)}</TileValue>
            </Tile>
            <Tile tone={Number(t.shifts_open) ? 'success' : undefined}>
              <TileLabel>Open shifts</TileLabel>
              <TileValue>{n(t.shifts_open)}</TileValue>
            </Tile>
            {/* Visitor passes (resident-portal Plan 3): how many are live, whether
                the building takes them, and how far its front desk may write
                them (migration 118). Counts and settings only — never a name. */}
            <Tile tone={b.visitor_passes_enabled === false ? 'warning' : undefined}>
              <TileLabel>Live visitor passes</TileLabel>
              <TileValue>{n(t.passes_live)}</TileValue>
              <TileFoot>
                {b.visitor_passes_enabled === false ? 'Switched off' : 'On'}
                {b.concierge_pass_access ? ` · desk: ${labelFor(b.concierge_pass_access)}` : ''}
              </TileFoot>
            </Tile>
            {/* Lobbero Stays (plan S1): live listings here and the building's
                consent switch. Counts only — never a listing name. */}
            {staysOn ? (<>
            <Tile data-testid="admin-building-stays-tile">
              <TileLabel>Stays listings</TileLabel>
              <TileValue>{n(t.stays_listings_active)}</TileValue>
              <TileFoot>{b.stays_enabled ? 'Switched on' : 'Switched off'}</TileFoot>
            </Tile>
            {/* Lobbero Stays (plan S4, R-S4.38): counts only. */}
            <Tile data-testid="admin-building-stays-upcoming-tile">
              <TileLabel>Upcoming stays</TileLabel>
              <TileValue>{n(t.stays_upcoming)}</TileValue>
            </Tile>
            <Tile data-testid="admin-building-stays-total-tile">
              <TileLabel>Stays</TileLabel>
              <TileValue>{n(t.stays_total)}</TileValue>
            </Tile>
            <Tile data-testid="admin-building-stays-blocks-tile">
              <TileLabel>Upcoming owner blocks</TileLabel>
              <TileValue>{n(t.stays_blocks_upcoming)}</TileValue>
            </Tile>
            </>) : null}
          </TileGrid>
        </Panel>
      </Split>

      <Panel title="Staff who can reach it">
        <AdminTable<Row>
          rows={data.staff || []}
          rowKey={r => String(r.id)}
          emptyLabel="Nobody is assigned to this building."
          onRowClick={r => nav(`/admin/users/${encodeURIComponent(r.id)}`)}
          columns={[
            { key: 'n', header: 'Name', render: r => r.full_name || <Id value={r.id} /> },
            { key: 'e', header: 'Email', render: r => r.email },
            { key: 'r', header: 'Role', render: r => (r.role ? <Tag tone="brand">{r.role.replace('_', ' ')}</Tag> : '—') },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
          ]}
        />
      </Panel>

      <Panel title="Units">
        <AdminTable<Row>
          rows={units.items}
          rowKey={r => String(r.id)}
          loading={units.loading}
          total={units.total}
          hasMore={units.hasMore}
          onLoadMore={units.loadMore}
          emptyLabel="No units."
          columns={[
            { key: 'u', header: 'Unit', render: r => r.unit_number },
            { key: 'k', header: 'Kind', wide: true, render: r => r.kind || '—' },
            { key: 'f', header: 'Floor', wide: true, render: r => r.floor || '—' },
            { key: 's', header: 'Status', render: r => <StatusTag value={r.status} /> },
            {
              key: 'o',
              header: 'Occupants',
              numeric: true,
              render: r => (Number(r.occupants) ? n(r.occupants) : <span style={{ color: palette.slateLight }}>vacant</span>),
            },
          ]}
        />
      </Panel>

      <Panel title="Current residents">
        <AdminTable<Row>
          rows={residents.items}
          rowKey={r => String(r.id)}
          loading={residents.loading}
          total={residents.total}
          hasMore={residents.hasMore}
          onLoadMore={residents.loadMore}
          emptyLabel="Nobody currently lives here."
          columns={[
            { key: 'n', header: 'Resident', render: r => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—' },
            { key: 'u', header: 'Unit', render: r => r.unit_number || '—' },
            { key: 'r', header: 'Relationship', wide: true, render: r => r.relationship || '—' },
            { key: 'i', header: 'Moved in', render: r => (r.moved_in ? String(r.moved_in).slice(0, 10) : '—') },
            {
              key: 'a',
              header: 'Account',
              render: r => (r.user_id ? <StatusTag value={r.user_status} /> : 'no login'),
            },
          ]}
        />
      </Panel>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM ADMINS
   ═══════════════════════════════════════════════════════════════════════════ */

export const AdminsScreen: React.FC<ActionHost> = ({ requestAction }) => {
  const { data, loading, error, reload } = useAdminResource<Row>('/admins');
  const [grantId, setGrantId] = useState('');
  const nav = useNavigate();

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading || !data) return <Loading />;

  const active = (data.items || []).filter((r: Row) => !r.revoked_at);

  return (
    <>
      <NoteBox>
        A platform administrator can read <strong>every organization on the platform</strong>.
        Granting and revoking are audited, and the last active administrator cannot be
        revoked — the console has no way to grant itself back.
      </NoteBox>

      <Card>
        <CardHead>
          <CardTitle>Grant access</CardTitle>
        </CardHead>
        <CardBody>
          <Toolbar>
            <input
              value={grantId}
              onChange={e => setGrantId(e.target.value)}
              placeholder="User id (find it on a person's page)"
              aria-label="User id"
              style={{
                flex: 1,
                minWidth: 260,
                height: 34,
                padding: '0 8px',
                border: `1px solid ${palette.lineStrong}`,
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 16,
              }}
            />
            <Btn
              variant="primary"
              disabled={!grantId.trim()}
              onClick={() =>
                requestAction({
                  title: 'Grant platform administrator',
                  body: (
                    <>
                      This account will be able to read every organization, every building and
                      every resident on the platform.
                    </>
                  ),
                  typeToConfirm: grantId.trim(),
                  confirmLabel: 'Grant access',
                  danger: true,
                  onConfirm: async reason => {
                    await api.grantAdmin(grantId.trim(), reason);
                    toast.success('Platform admin granted');
                    setGrantId('');
                    reload();
                  },
                })
              }
            >
              Grant
            </Btn>
          </Toolbar>
        </CardBody>
      </Card>

      <Panel title={`Administrators (${active.length} active)`}>
        <AdminTable<Row>
          rows={data.items || []}
          rowKey={r => String(r.user_id)}
          emptyLabel="No platform administrators."
          columns={[
            { key: 'n', header: 'Name', render: r => r.full_name || <Id value={r.user_id} /> },
            { key: 'e', header: 'Email', render: r => r.email },
            {
              key: 's',
              header: 'State',
              render: r =>
                r.revoked_at ? <Tag tone="alert">revoked {when(r.revoked_at)}</Tag> : <Tag tone="success">active</Tag>,
            },
            { key: 'g', header: 'Granted', render: r => when(r.granted_at) },
            { key: 'b', header: 'By', wide: true, render: r => <Id value={r.granted_by} /> },
            { key: 'r', header: 'Note', wide: true, render: r => r.note || '—' },
            {
              key: 'a',
              header: '',
              render: r =>
                r.revoked_at ? null : (
                  <Btn
                    variant="danger"
                    disabled={active.length <= 1}
                    title={active.length <= 1 ? 'This is the last administrator.' : undefined}
                    onClick={e => {
                      e.stopPropagation();
                      requestAction({
                        title: `Revoke ${r.full_name || r.user_id}`,
                        body: <>They lose access to this console on their very next request.</>,
                        confirmLabel: 'Revoke access',
                        danger: true,
                        onConfirm: async reason => {
                          await api.revokeAdmin(r.user_id, reason);
                          toast.success('Platform admin revoked');
                          reload();
                        },
                      });
                    }}
                  >
                    Revoke
                  </Btn>
                ),
            },
          ]}
          onRowClick={r => nav(`/admin/users/${encodeURIComponent(r.user_id)}`)}
        />
      </Panel>
    </>
  );
};

export { ConfirmDialog };
export type { ConfirmSpec };
